import React, { useState } from 'react';
import { format, isSameDay } from 'date-fns';
import { Upload, Trophy, ChevronRight } from 'lucide-react';
import { DS, GS, hairline, eyebrow } from '../../lib/designTokens';
import { isGameLive } from '../../lib/game-utils';

const initialsOf = (name: string) =>
  (name || '?').trim().split(/\s+/).filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase();

const TeamSide = ({ name, logoURL, record, dark }: { name: string; logoURL?: string; record?: string; dark?: boolean }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', flex: 1, minWidth: 0 }}>
    {logoURL ? (
      <img src={logoURL} alt={name} style={{ width: '44px', height: '44px', objectFit: 'contain' }} />
    ) : (
      <div style={{
        width: '44px', height: '44px', borderRadius: '50%',
        backgroundColor: dark ? DS.textPri : DS.bgAlt,
        border: dark ? 'none' : hairline,
        color: dark ? '#FFFFFF' : DS.textSec,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '14px', fontWeight: 600,
      }}>
        {initialsOf(name)}
      </div>
    )}
    <span style={{ fontSize: '12px', fontWeight: 600, color: DS.textPri, textAlign: 'center', maxWidth: '110px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {name}
    </span>
    <span style={{ fontSize: '11px', fontWeight: 400, color: DS.textSec, minHeight: '13px' }}>
      {record || ''}
    </span>
  </div>
);

// Minimal box score from game.stats — shows every player with at least 1 AB or any pitching
const dash = (v: any) => (v == null || v === 0 ? '—' : v);
const fmt = (v: any) => (v == null ? '—' : v);

// Stat columns are fixed-width; name column fills remaining space.
const STAT_W = 32;
const STAT_COLS = 6;

const BoxScore = ({ game, players }: { game: any; players: any[] }) => {
  if (!game.stats || Object.keys(game.stats).length === 0) return null;

  const allRows = Object.entries(game.stats as Record<string, any>)
    .map(([pid, s]) => {
      const player = players.find((p: any) => p.id === pid);
      if (!player || !s) return null;
      const hasBatting = (s.ab || 0) > 0 || (s.pa || 0) > 0 || (s.h || 0) > 0 || (s.r || 0) > 0;
      const hasPitching = (s.ip || 0) > 0;
      if (!hasBatting && !hasPitching) return null;
      return { player, s, hasBatting, hasPitching };
    })
    .filter(Boolean) as { player: any; s: any; hasBatting: boolean; hasPitching: boolean }[];

  if (allRows.length === 0) return null;

  const batters = allRows.filter(r => r.hasBatting)
    .sort((a, b) => (b.s.h || 0) - (a.s.h || 0) || (b.s.rbi || 0) - (a.s.rbi || 0));
  const pitchers = allRows.filter(r => r.hasPitching)
    .sort((a, b) => (b.s.ip || 0) - (a.s.ip || 0));

  // Team totals — sum across all players
  const battingTotals = batters.reduce((acc, { s }) => ({
    ab:  acc.ab  + (s.ab  || 0),
    h:   acc.h   + (s.h   || 0),
    rbi: acc.rbi + (s.rbi || 0),
    r:   acc.r   + (s.r   || 0),
    bb:  acc.bb  + (s.bb  || 0),
    so:  acc.so  + (s.so  || 0),
  }), { ab: 0, h: 0, rbi: 0, r: 0, bb: 0, so: 0 });

  // IP totals need baseball arithmetic: sum outs, then convert back to X.Y format
  const pitchingTotals = pitchers.reduce((acc, { s }) => {
    const ipWhole = Math.floor(s.ip || 0);
    const ipOuts  = Math.round(((s.ip || 0) - ipWhole) * 10); // 0.1 = 1 out, 0.2 = 2 outs
    return {
      outs:       acc.outs       + ipWhole * 3 + ipOuts,
      soAllowed:  acc.soAllowed  + (s.soAllowed  || 0),
      bbAllowed:  acc.bbAllowed  + (s.bbAllowed  || 0),
      erAllowed:  acc.erAllowed  + (s.erAllowed  || 0),
      hitsAllowed:acc.hitsAllowed+ (s.hitsAllowed|| 0),
      pitches:    acc.pitches    + (s.pitches    || 0),
    };
  }, { outs: 0, soAllowed: 0, bbAllowed: 0, erAllowed: 0, hitsAllowed: 0, pitches: 0 });
  const totalIP = `${Math.floor(pitchingTotals.outs / 3)}.${pitchingTotals.outs % 3}`;

  const lastName = (name: string) => name.trim().split(/\s+/).filter(Boolean).pop() || name;

  // Shared styles
  const thSection: React.CSSProperties = {
    fontSize: '9px', fontWeight: 700, color: DS.textMuted,
    textTransform: 'uppercase', letterSpacing: '0.08em',
    textAlign: 'left', padding: '10px 0 4px', whiteSpace: 'nowrap',
  };
  const thStat: React.CSSProperties = {
    fontSize: '9px', fontWeight: 600, color: DS.textMuted,
    textTransform: 'uppercase', letterSpacing: '0.06em',
    textAlign: 'right', padding: '0 0 4px 0',
    width: `${STAT_W}px`, minWidth: `${STAT_W}px`,
  };
  const thName: React.CSSProperties = {
    fontSize: '9px', fontWeight: 600, color: DS.textMuted,
    textTransform: 'uppercase', letterSpacing: '0.06em',
    textAlign: 'left', padding: '0 12px 4px 0',
  };
  const tdName: React.CSSProperties = {
    fontSize: '12px', fontWeight: 600, color: DS.textPri,
    padding: '5px 12px 5px 0', whiteSpace: 'nowrap',
  };
  const tdStat = (bold = false): React.CSSProperties => ({
    fontSize: '12px', fontWeight: bold ? 700 : 400, color: DS.textPri,
    textAlign: 'right', padding: '5px 0',
    width: `${STAT_W}px`, minWidth: `${STAT_W}px`,
  });

  const Table = ({ children }: { children: React.ReactNode }) => (
    <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'auto' }}>
      {children}
    </table>
  );

  return (
    <div style={{ borderTop: hairline, padding: '4px 16px 8px' }}>
      <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>

        {/* ── Batting ── */}
        {batters.length > 0 && (
          <Table>
            <thead>
              <tr><th colSpan={STAT_COLS + 1} style={thSection}>Hitting</th></tr>
              <tr>
                <th style={thName}></th>
                <th style={thStat}>AB</th>
                <th style={thStat}>H</th>
                <th style={thStat}>RBI</th>
                <th style={thStat}>R</th>
                <th style={thStat}>BB</th>
                <th style={thStat}>K</th>
              </tr>
            </thead>
            <tbody>
              {batters.map(({ player, s }) => (
                <tr key={player.id} style={{ borderTop: hairline }}>
                  <td style={tdName}>
                    <span style={{ fontSize: '10px', color: DS.textMuted, marginRight: '5px' }}>#{player.number}</span>
                    {lastName(player.name)}
                  </td>
                  <td style={tdStat()}>{fmt(s.ab)}</td>
                  <td style={tdStat((s.h || 0) > 0)}>{fmt(s.h)}</td>
                  <td style={tdStat((s.rbi || 0) > 0)}>{fmt(s.rbi)}</td>
                  <td style={tdStat()}>{fmt(s.r)}</td>
                  <td style={tdStat()}>{fmt(s.bb)}</td>
                  <td style={tdStat()}>{dash(s.so)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: `1.5px solid ${DS.textPri}` }}>
                <td style={{ ...tdName, fontSize: '11px', color: DS.textSec }}>Totals</td>
                <td style={tdStat(true)}>{battingTotals.ab}</td>
                <td style={tdStat(true)}>{battingTotals.h}</td>
                <td style={tdStat(true)}>{battingTotals.rbi}</td>
                <td style={tdStat(true)}>{battingTotals.r}</td>
                <td style={tdStat(true)}>{battingTotals.bb}</td>
                <td style={tdStat(true)}>{battingTotals.so || '—'}</td>
              </tr>
            </tfoot>
          </Table>
        )}

        {/* ── Pitching ── */}
        {pitchers.length > 0 && (
          <Table>
            <thead>
              <tr><th colSpan={STAT_COLS + 1} style={thSection}>Pitching</th></tr>
              <tr>
                <th style={thName}></th>
                <th style={thStat}>IP</th>
                <th style={thStat}>K</th>
                <th style={thStat}>BB</th>
                <th style={thStat}>ER</th>
                <th style={thStat}>H</th>
                <th style={thStat}>PC</th>
              </tr>
            </thead>
            <tbody>
              {pitchers.map(({ player, s }) => (
                <tr key={`p-${player.id}`} style={{ borderTop: hairline }}>
                  <td style={tdName}>
                    <span style={{ fontSize: '10px', color: DS.textMuted, marginRight: '5px' }}>#{player.number}</span>
                    {lastName(player.name)}
                  </td>
                  <td style={tdStat(true)}>{s.ip != null ? Number(s.ip).toFixed(1) : '—'}</td>
                  <td style={tdStat((s.soAllowed || 0) > 0)}>{dash(s.soAllowed)}</td>
                  <td style={tdStat()}>{dash(s.bbAllowed)}</td>
                  <td style={tdStat()}>{dash(s.erAllowed)}</td>
                  <td style={tdStat()}>{dash(s.hitsAllowed)}</td>
                  <td style={tdStat()}>{dash(s.pitches)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: `1.5px solid ${DS.textPri}` }}>
                <td style={{ ...tdName, fontSize: '11px', color: DS.textSec }}>Totals</td>
                <td style={tdStat(true)}>{totalIP}</td>
                <td style={tdStat(true)}>{pitchingTotals.soAllowed || '—'}</td>
                <td style={tdStat(true)}>{pitchingTotals.bbAllowed || '—'}</td>
                <td style={tdStat(true)}>{pitchingTotals.erAllowed || '—'}</td>
                <td style={tdStat(true)}>{pitchingTotals.hitsAllowed || '—'}</td>
                <td style={tdStat(true)}>{pitchingTotals.pitches || '—'}</td>
              </tr>
            </tfoot>
          </Table>
        )}

      </div>
    </div>
  );
};

export const MatchupHero = ({ game, team, recordString, players, onPlan, onScouting, onLive, onUploadGameStats, onOpenTournament, opponentIntel, topThreat }: {
  game: any;
  team: any;
  recordString: string;
  players: any[];
  onPlan: (game: any) => void;
  onScouting: (game: any) => void;
  onLive: (game: any) => void;
  onUploadGameStats: (game: any) => void;
  onOpenTournament?: (tournamentId: string) => void;
  opponentIntel?: { meetings: number; knownPlayers: number; opponentName: string } | null;
  topThreat?: { name: string; number: string } | null;
}) => {
  if (!game) return null;

  const [showBoxScore, setShowBoxScore] = useState(false);

  const d = new Date(game.date);
  const live = isGameLive(game);
  const completed = game.status === 'completed';
  const gameDay = isSameDay(d, new Date());

  const sp = game.plan?.startingPitcherId
    ? players.find((p: any) => p.id === game.plan.startingPitcherId)
    : null;

  const hasStats = completed && game.stats && Object.keys(game.stats).length > 0;

  // ── Top performers (completed games, our roster only) ────────────────────
  const topPerformers = (() => {
    if (!hasStats) return [];
    return Object.entries(game.stats as Record<string, any>)
      .map(([pid, s]) => {
        const player = players.find((p: any) => p.id === pid);
        if (!player || !s) return null;

        const battingScore = (s.h || 0) * 2 + (s.xbh || 0) * 1.5 + (s.rbi || 0) * 1.5
          + (s.r || 0) + (s.bb || 0) * 0.75 + (s.sb || 0) * 0.5;
        const pitchingScore = (s.ip || 0) * 2 + (s.soAllowed || 0) - (s.erAllowed || 0) * 1.5;
        const score = battingScore + Math.max(0, pitchingScore);
        if (score <= 0) return null;

        const lead = (s.ab || 0) > 0 ? `${s.h || 0}-${s.ab}` : '';
        const detail: string[] = [];
        if (s.xbh) detail.push(`${s.xbh} XBH`);
        if (s.rbi) detail.push(`${s.rbi} RBI`);
        if (s.r) detail.push(`${s.r} R`);
        if (s.bb) detail.push(`${s.bb} BB`);
        if (s.sb) detail.push(`${s.sb} SB`);
        if ((s.ip || 0) > 0) {
          detail.push(`${s.ip} IP`);
          if (s.soAllowed) detail.push(`${s.soAllowed} K`);
          if (s.erAllowed != null) detail.push(`${s.erAllowed} ER`);
        }
        const line = [lead, detail.join(', ')].filter(Boolean).join(' | ');
        if (!line) return null;

        return { player, line, score };
      })
      .filter(Boolean)
      .sort((a: any, b: any) => b.score - a.score)
      .slice(0, 3) as { player: any; line: string; score: number }[];
  })();

  const us = { name: team.name, logoURL: team.logoURL, record: recordString, dark: true };
  const them = { name: game.opponent, dark: false };
  const left = game.isHome ? them : us;
  const right = game.isHome ? us : them;

  const myScore = game.result?.myScore ?? 0;
  const oppScore = game.result?.opponentScore ?? 0;
  const leftScore = game.isHome ? oppScore : myScore;
  const rightScore = game.isHome ? myScore : oppScore;

  const liveEnabled = !completed && (live || gameDay);

  // Prominent segmented buttons. `primary` renders filled (dark); others render as outlined pills.
  const actionStyle = (enabled: boolean, primary = false): React.CSSProperties => ({
    flex: 1,
    fontSize: '13px',
    fontWeight: 700,
    letterSpacing: '0.01em',
    color: !enabled ? DS.dim : primary ? '#FFFFFF' : DS.textPri,
    background: !enabled ? DS.bgAlt : primary ? DS.textPri : '#FFFFFF',
    border: `1.5px solid ${!enabled ? DS.dim : DS.textPri}`,
    borderRadius: '10px',
    cursor: enabled ? 'pointer' : 'default',
    padding: '11px 8px',
    textAlign: 'center',
    opacity: enabled ? 1 : 0.5,
  });

  // "Plan" always opens the game planner — including tournament games, which
  // used to be redirected to the tournament dashboard (no lineup builder there,
  // so the coach had nowhere to set a batting order). The tournament dashboard
  // is still one tap away via the Tournament chip above the matchup.
  const handlePlan = () => onPlan(game);

  return (
    <div style={{ backgroundColor: DS.bg }}>

      {/* Tournament context — this game belongs to a tournament, so give the
          coach a way back to the weekend dashboard (pitching + round robin). */}
      {game.tournamentId && onOpenTournament && (
        <button
          onClick={() => onOpenTournament(game.tournamentId)}
          style={{
            display: 'flex', alignItems: 'center', gap: '4px',
            margin: '12px 16px 0', padding: 0,
            background: 'none', border: 'none', cursor: 'pointer',
          }}
        >
          <Trophy style={{ width: '12px', height: '12px', color: '#C9A84C' }} />
          <span style={{ fontSize: '10px', fontWeight: 700, color: '#B8912E', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
            Tournament
          </span>
          <ChevronRight style={{ width: '11px', height: '11px', color: '#B8912E' }} />
        </button>
      )}

      {/* Matchup row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', padding: '16px 16px 12px' }}>
        <TeamSide {...left} />

        {/* Center column */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexShrink: 0, padding: '4px 12px 0', minWidth: '110px' }}>
          {completed && game.result ? (
            <>
              <span style={{ ...eyebrow, fontSize: '10px', marginBottom: '4px' }}>Final</span>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                <span style={{ fontSize: '34px', fontWeight: 600, lineHeight: 1, color: leftScore >= rightScore ? DS.textPri : DS.dim }}>
                  {leftScore}
                </span>
                <span style={{ fontSize: '20px', fontWeight: 400, color: DS.textMuted }}>–</span>
                <span style={{ fontSize: '34px', fontWeight: 600, lineHeight: 1, color: rightScore >= leftScore ? DS.textPri : DS.dim }}>
                  {rightScore}
                </span>
              </div>
            </>
          ) : live ? (
            <>
              <span style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '4px' }}>
                <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: GS.green, display: 'inline-block' }} />
                <span style={{ fontSize: '11px', fontWeight: 600, color: DS.textPri, letterSpacing: '0.04em' }}>LIVE</span>
              </span>
              {game.currentInning && (
                <span style={{ fontSize: '15px', fontWeight: 600, color: DS.textPri }}>Inning {game.currentInning}</span>
              )}
            </>
          ) : (
            <>
              <span style={{ ...eyebrow, fontSize: '10px', marginBottom: '4px' }}>{format(d, 'EEE, MMM d')}</span>
              <span style={{ fontSize: '15px', fontWeight: 600, color: DS.textPri }}>
                {game.time || format(d, 'h:mm a')}
              </span>
            </>
          )}
        </div>

        <TeamSide {...right} />
      </div>

      {/* Pitcher row — scheduled/live only */}
      {!completed && (
        <div style={{ borderTop: hairline, padding: '10px 16px' }}>
          <div style={{ ...eyebrow, fontSize: '10px', textAlign: 'center', marginBottom: '6px' }}>Starting Pitchers</div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ flex: 1, textAlign: 'center' }}>
              <span style={{ fontSize: '13px', fontWeight: game.isHome ? 400 : 600, color: game.isHome ? DS.textMuted : (sp ? DS.textPri : DS.textMuted) }}>
                {game.isHome ? 'TBD' : (sp ? `#${sp.number} ${sp.name}` : 'TBD')}
              </span>
            </div>
            <div style={{ flex: 1, textAlign: 'center' }}>
              <span style={{ fontSize: '13px', fontWeight: game.isHome ? 600 : 400, color: game.isHome ? (sp ? DS.textPri : DS.textMuted) : DS.textMuted }}>
                {game.isHome ? (sp ? `#${sp.number} ${sp.name}` : 'TBD') : 'TBD'}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Opponent intel strip — scheduled/live games where we have history */}
      {!completed && opponentIntel && opponentIntel.meetings > 0 && (
        <button
          onClick={() => onScouting(game)}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            width: '100%', borderTop: hairline,
            padding: '10px 16px',
            background: GS.greenSoft, border: 'none', cursor: 'pointer', textAlign: 'left',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '12px' }}>🗂️</span>
            <div>
              <span style={{ fontSize: '12px', fontWeight: 700, color: GS.greenInk }}>
                Intel on {opponentIntel.opponentName}
              </span>
              <span style={{ fontSize: '12px', fontWeight: 400, color: GS.greenInk, opacity: 0.8 }}>
                {' '}— {opponentIntel.meetings} game{opponentIntel.meetings !== 1 ? 's' : ''} scouted
                {opponentIntel.knownPlayers > 0 ? `, ${opponentIntel.knownPlayers} batters on file` : ''}
              </span>
              {topThreat && (
                <span style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: GS.greenInk, opacity: 0.9, marginTop: '2px' }}>
                  Watch {topThreat.number ? `#${topThreat.number} ` : ''}{topThreat.name}
                </span>
              )}
            </div>
          </div>
          <span style={{ fontSize: '12px', fontWeight: 600, color: GS.greenInk, flexShrink: 0 }}>View →</span>
        </button>
      )}

      {/* Top performers — completed with stats */}
      {topPerformers.length > 0 && (
        <div style={{ borderTop: hairline, padding: '12px 16px 14px' }}>
          <div style={{ fontSize: '14px', fontWeight: 600, color: DS.textPri, textAlign: 'center', marginBottom: '12px' }}>
            Top Performers
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
            {topPerformers.map(({ player, line }) => (
              <div key={player.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, maxWidth: '140px', minWidth: 0 }}>
                <div style={{
                  width: '52px', height: '52px', borderRadius: '50%',
                  backgroundColor: DS.textPri, color: '#FFFFFF',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '17px', fontWeight: 600,
                }}>
                  {player.number}
                </div>
                <span style={{ fontSize: '14px', fontWeight: 600, color: DS.textPri, marginTop: '8px', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {player.name.trim().split(/\s+/).filter(Boolean).pop() || player.name}
                </span>
                <span style={{ fontSize: '11px', fontWeight: 400, color: DS.textSec, marginTop: '2px', textAlign: 'center', lineHeight: 1.4 }}>
                  {line}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Box score — toggle */}
      {hasStats && showBoxScore && (
        <BoxScore game={game} players={players} />
      )}

      {/* Upload stats prompt — completed game with no stats yet */}
      {completed && !hasStats && (
        <div style={{ borderTop: hairline, padding: '12px 16px' }}>
          <button
            onClick={() => onUploadGameStats(game)}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              width: '100%', padding: '10px', borderRadius: '8px',
              backgroundColor: DS.bgAlt, border: hairline,
              cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: DS.textSec,
            }}
          >
            <Upload style={{ width: '14px', height: '14px' }} />
            Upload Game Stats
          </button>
        </div>
      )}

      {/* Action row — the coach's command center (Plan · Scouting · Live) */}
      <div style={{ borderTop: hairline, display: 'flex', gap: '8px', padding: '12px 16px' }}>
        <button onClick={handlePlan} style={actionStyle(true, !completed)}>
          {completed ? 'Review' : 'Plan'}
        </button>
        <button onClick={() => onScouting(game)} style={actionStyle(true)}>
          Scouting
        </button>
        {completed ? (
          hasStats ? (
            <>
              <button onClick={() => setShowBoxScore(v => !v)} style={actionStyle(true)}>
                {showBoxScore ? 'Hide Stats' : 'Box Score'}
              </button>
              <button onClick={() => onUploadGameStats(game)} style={actionStyle(true)}>
                Upload Stats
              </button>
            </>
          ) : (
            <span style={actionStyle(false)}>Final</span>
          )
        ) : (
          <button onClick={() => liveEnabled && onLive(game)} style={actionStyle(liveEnabled, live)} disabled={!liveEnabled}>
            {live ? 'Resume Live' : 'Live'}
          </button>
        )}
      </div>
    </div>
  );
};
